import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Clock, Plus, Trash2 } from 'lucide-react';
import { Textarea } from './textarea';
import { Button } from './button';
import { Badge } from './badge';

// Parse content with attributions
// Format: [Author Name|timestamp]: content
const parseAttributedContent = (content) => {
  if (!content) return [];
  
  const blocks = [];
  const regex = /\[([^\]|]+)\|?([^\]]*)\]:\s*([\s\S]*?)(?=\n\[|$)/g;
  let match;
  let lastIndex = 0;
  
  while ((match = regex.exec(content)) !== null) {
    // Check for unattributed content before this match
    if (match.index > lastIndex) {
      const unattributed = content.substring(lastIndex, match.index).trim();
      if (unattributed) {
        blocks.push({
          author: 'Anonymous',
          timestamp: null,
          content: unattributed,
          isUnattributed: true
        });
      }
    }
    
    blocks.push({
      author: match[1].trim(),
      timestamp: match[2].trim() || null,
      content: match[3].trim(),
      isUnattributed: false
    });
    
    lastIndex = regex.lastIndex;
  }
  
  // Check for remaining unattributed content
  if (lastIndex < content.length) {
    const remaining = content.substring(lastIndex).trim();
    if (remaining && !remaining.match(/^\[/)) {
      blocks.push({
        author: 'Anonymous',
        timestamp: null,
        content: remaining,
        isUnattributed: true
      });
    }
  }
  
  // If no attributed blocks found, treat whole content as unattributed
  if (blocks.length === 0 && content.trim()) {
    blocks.push({
      author: 'Anonymous',
      timestamp: null,
      content: content.trim(),
      isUnattributed: true
    });
  }
  
  return blocks;
};

// Format content with attributions
const formatAttributedContent = (blocks) => {
  return blocks.map(block => {
    if (block.isUnattributed || !block.author || block.author === 'Anonymous') {
      return block.content;
    }
    const timestamp = block.timestamp || new Date().toLocaleDateString();
    return `[${block.author}|${timestamp}]: ${block.content}`;
  }).join('\n\n');
};

// Get unique color for author
const getAuthorColor = (author) => {
  const colors = [
    { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-600' },
    { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-600' },
    { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-600' },
    { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-600' },
    { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-600' },
    { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-600' },
  ];
  
  // Simple hash to get consistent color
  let hash = 0;
  for (let i = 0; i < author.length; i++) {
    hash = author.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

export const CollaborativeEditor = ({
  content,
  onChange,
  userName,
  placeholder,
  isCollaborative = false,
  showAttribution = false,
  disabled = false,
  className = ''
}) => {
  const [blocks, setBlocks] = useState([]);
  const [editingBlockIndex, setEditingBlockIndex] = useState(null);
  const [newBlockContent, setNewBlockContent] = useState('');
  const [showAddBlock, setShowAddBlock] = useState(false);
  const textareaRef = useRef(null);
  
  // Parse content into blocks when collaborative mode
  useEffect(() => {
    if (isCollaborative && showAttribution) {
      const parsed = parseAttributedContent(content);
      setBlocks(parsed);
    }
  }, [content, isCollaborative, showAttribution]);
  
  // Handle adding a new attributed block
  const handleAddBlock = () => {
    if (!newBlockContent.trim()) return;
    
    const newBlock = {
      author: userName,
      timestamp: new Date().toLocaleDateString(),
      content: newBlockContent.trim(),
      isUnattributed: false
    };
    
    const updatedBlocks = [...blocks, newBlock];
    setBlocks(updatedBlocks);
    onChange(formatAttributedContent(updatedBlocks));
    setNewBlockContent('');
    setShowAddBlock(false);
  };
  
  // Handle editing an existing block
  const handleEditBlock = (index, newContent) => {
    const updatedBlocks = blocks.map((block, i) => 
      i === index ? { ...block, content: newContent } : block
    );
    setBlocks(updatedBlocks);
    onChange(formatAttributedContent(updatedBlocks));
  };
  
  // Handle deleting a block
  const handleDeleteBlock = (index) => {
    const updatedBlocks = blocks.filter((_, i) => i !== index);
    setBlocks(updatedBlocks);
    onChange(formatAttributedContent(updatedBlocks));
  };
  
  // If not collaborative or not showing attribution, use simple textarea
  if (!isCollaborative || !showAttribution) {
    return (
      <Textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`min-h-[200px] resize-y ${className}`}
        data-testid="sub-element-editor"
      />
    );
  }
  
  // Collaborative editor with attribution
  return (
    <div className={`space-y-3 ${className}`} data-testid="collaborative-editor">
      {/* Existing blocks */}
      <AnimatePresence mode="popLayout">
        {blocks.map((block, index) => {
          const colors = getAuthorColor(block.author);
          const isEditing = editingBlockIndex === index;
          const isOwnBlock = block.author === userName;
          
          return (
            <motion.div
              key={`${block.author}-${index}`}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`relative rounded-xl border-l-4 ${colors.border} ${colors.bg} p-4`}
            >
              {/* Attribution header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                    <User size={12} className={colors.text} />
                  </div>
                  <span className={`text-sm font-medium ${colors.text}`}>
                    {block.author}
                    {isOwnBlock && <span className="text-xs opacity-60 ml-1">(you)</span>}
                  </span>
                  {block.timestamp && (
                    <span className="text-xs text-[var(--fg)]/40 flex items-center gap-1">
                      <Clock size={10} />
                      {block.timestamp}
                    </span>
                  )}
                </div>
                
                {/* Actions for own blocks */}
                {isOwnBlock && !disabled && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingBlockIndex(isEditing ? null : index)}
                      className="h-6 px-2 text-xs"
                    >
                      {isEditing ? 'Done' : 'Edit'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBlock(index)}
                      className="h-6 px-2 text-xs text-[var(--danger)]"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Content */}
              {isEditing ? (
                <Textarea
                  value={block.content}
                  onChange={(e) => handleEditBlock(index, e.target.value)}
                  className="min-h-[80px] bg-white/50"
                  autoFocus
                />
              ) : (
                <p className="text-sm text-[var(--fg)]/80 whitespace-pre-wrap leading-relaxed">
                  {block.content}
                </p>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
      
      {/* Add new contribution */}
      {!disabled && (
        <div className="pt-2">
          {showAddBlock ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <div className="flex items-center gap-2 text-sm text-[var(--fg)]/60">
                <User size={14} />
                <span>Adding as <strong>{userName}</strong></span>
              </div>
              <Textarea
                value={newBlockContent}
                onChange={(e) => setNewBlockContent(e.target.value)}
                placeholder="Share your personal statement... What are your desires, needs, accomplishments? What do you love most about life?"
                className="min-h-[120px]"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAddBlock}
                  disabled={!newBlockContent.trim()}
                >
                  Add My Contribution
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddBlock(false);
                    setNewBlockContent('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddBlock(true)}
              className="w-full border-dashed"
              data-testid="add-contribution-button"
            >
              <Plus size={16} className="mr-2" />
              Add Your Voice
            </Button>
          )}
        </div>
      )}
      
      {/* Empty state */}
      {blocks.length === 0 && !showAddBlock && (
        <div className="text-center py-8 text-[var(--fg)]/50">
          <User size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No contributions yet</p>
          <p className="text-xs">Be the first to share your personal statement</p>
        </div>
      )}
    </div>
  );
};

export default CollaborativeEditor;
